import { type FC, useState } from "react";
import {
  AppBar,
  Box,
  Card,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStreamsListQuery } from "@/partner/entities/streams";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ContentTypeType } from "@/types/graphql/partner.generated";
import { StreamsListItem } from "./StreamsListItem";
import { StreamsToolbar } from "./StreamsToolbar";
import { buildStreamsFilter } from "../model/buildStreamsFilter";

interface StreamsListProps {
  onEditStream: (id: string) => void;
}

export const StreamsList: FC<StreamsListProps> = ({ onEditStream }) => {
  const { t } = useTranslation();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter,
  } = useFilter();

  const { data, loading } = useStreamsListQuery({
    variables: {
      page,
      rowsPerPage,
      filter: {
        ...buildStreamsFilter(filter),
        type: ContentTypeType.StreamType,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const streamsData = data?.contents;
  const streams = streamsData?.nodes ?? [];
  const count = streamsData?.count ?? 0;

  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const selectedIds = selectedItems.map((item) => item.id);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(streams.map((item) => ({ id: item.id })));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      setSelectedItems([...selectedItems, { id }]);
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < streams.length;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (streams.length === 0) {
    return <EmptyListCard title={t("streams.list.empty")} />;
  }

  return (
    <Box>
      <Card elevation={0}>
        <AppBar position="static" color="default" elevation={0}>
          <StreamsToolbar
            indeterminate={indeterminate}
            numSelected={numSelected}
            nodes={streams}
            onSelectAllClick={handleSelectAll}
            selectedItems={selectedItems}
            onCloseSelected={() => setSelectedItems([])}
          />
        </AppBar>
      </Card>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {streams.map((item) => (
          <StreamsListItem
            key={item.id}
            item={item}
            selected={selectedIds.includes(item.id)}
            onSelectOneChange={handleSelectOne}
            onClickItem={onEditStream}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
